import {parseInteger} from '@benchling/js-utils';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  Batch,
  Container,
  ContainerSummary,
  Grid,
  GridCellSummary,
  GridSummary,
  GridType,
  StorableSummary,
} from 'constants/SampleConstants';
import {SampleRoles} from 'constants/SampleStatusConstants';
import {SchemaTag, SchemaTagsDict, Tag} from 'folder-items/FileConstants';
import * as util from 'notebook/tables/excess/Util';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {
  PLATE_MAP_MAX_GRID_HEIGHT,
  PLATE_MAP_MAX_GRID_WIDTH,
  PlateMapSize,
} from 'samples/plate-maps/PlateMapConstants';
import {getFlattenedWellIndex, getWellPositionFromIndex} from 'samples/plate-maps/PlateMapUtil';
import {GridSchema, PLATE_SCHEMA_TYPE, PlateSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {ListableItem} from 'search/SearchConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import ErrorSubclass from 'util/ErrorSubclass';
import {Optional} from 'util/Types';

import {ContainerSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/ContainerSummaryGraphQL';
import {StorableSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';

export class InvalidPositionError extends ErrorSubclass {}

export class NoPositionError extends InvalidPositionError {}

export class InvalidPositionFormatError extends InvalidPositionError {}

export class PositionOutOfBoundsError extends InvalidPositionError {}

const GRID_ALPHANUMERIC_POSITION_REGEX = /^([A-Za-z]+)(\d+)$/;
const GRID_INTEGER_POSITION_REGEX = /^(\d+)$/;

export interface Position {
  rowIndex: number;
  columnIndex: number;
}

export function getStorableLabel(storable: StorableSummary): string {
  return storable.barcode ?? storable.name;
}

export function getStorableLabelGraphQL(storable: StorableSummaryGraphQL): string {
  return storable.barcode?.value ?? storable.name;
}

export function getContainerLabelWithQuantity(container: ContainerSummary): string {
  const quantityText =
    container.quantity.value == null
      ? `unspecified ${getQuantityDisplayString(false)}`
      : `${container.quantity.value} ${container.quantity.units}`;
  return `${getStorableLabel(container)} (${quantityText})`;
}

export function getContainerLabelWithQuantityGraphQL(container: ContainerSummaryGraphQL): string {
  const quantityText =
    container.quantityJson.value == null
      ? `unspecified ${getQuantityDisplayString(false)}`
      : `${container.quantityJson.value} ${container.quantityJson.units}`;

  return `${getStorableLabelGraphQL(container)} (${quantityText})`;
}

export function tagsToDict(tags: Array<SchemaTag>): SchemaTagsDict {
  const schemaTagDict: SchemaTagsDict = {};
  for (const tag of tags) {
    schemaTagDict[tag.tagSchemaField.id] = tag;
  }
  return schemaTagDict;
}

function getTagValueForCsv(batch: Batch, field: SchemaField): string {
  const tag = tagsToDict(batch.tags)[field.id];
  const value = tag.value ?? '';
  return value.includes(',') ? `"${value}"` : value;
}

/*
 * Return a comma-separated value of the batch's name and tag values.
 * Optional @{fields} to indicate the fields whose values should be returned. Defaults to
 * fields in {batch.tags}.
 */
export function getBatchClipboardData(batch: Batch, fields?: Array<SchemaField>): string {
  const batchFields = fields ?? batch.tags.map(({tagSchemaField}) => tagSchemaField);
  return [batch.name].concat(batchFields.map((field) => getTagValueForCsv(batch, field))).join(', ');
}

/*
 * Return a formatted title that includes the file name, and batch name for containers.
 */
export function getSampleFileTitle(sample: Batch | Container): string {
  let title = '';
  if (isContainer(sample) && sample.batch) {
    title = `${sample.batch.file.name} / ${sample.batch.name}`;
  } else if (isBatch(sample)) {
    title = sample.file.name;
  }
  if (title) {
    return `${title} /`;
  }
  return title;
}

/*
 * Return the given sample's file `iconId`.
 */
export function getSampleFileIconId(sample: Batch | Container): BenchlingIconName {
  if (isContainer(sample) && sample.batch?.file && sample.batch.file.tagSchema) {
    return sample.batch.file.tagSchema.iconId;
  } else if (isBatch(sample) && sample.file.tagSchema) {
    return sample.file.tagSchema.iconId;
  }
  assertAndContinue(false, `Unexpected iconId for sample file, using 'question' as placeholder`, {sample});
  return 'question';
}

/*
 * Return a list of all tags associated with the sample, including batch-level and file-level tags.
 */
export function getAllTags(sample: Batch | Container): Array<Tag> {
  let batchTags: Array<SchemaTag> = [];
  let fileTags: Array<Tag> = [];
  let allTags: Array<Tag> = [];

  if (isContainer(sample)) {
    batchTags = sample.batch?.tags ?? [];
    fileTags = sample.batch?.file.tags ?? [];
  } else if (isBatch(sample)) {
    fileTags = sample.file.tags;
  }

  allTags = sample.tags.concat(batchTags);

  return allTags.concat(fileTags);
}

export function isMatrixPlateSchema(gridSchema?: PlateSchema | null): boolean {
  return gridSchema != null && gridSchema.plateSchemaType === PLATE_SCHEMA_TYPE.MATRIX_PLATE;
}

export function isGridId(storableId: string): boolean {
  return APIUtil.isPlate(storableId) || APIUtil.isBox(storableId);
}

export function isGrid(storable?: StorableSummary | null): storable is GridSummary {
  return storable != null && isGridId(storable.id);
}

export function getStorableTypeNameForId(storableId: string): string {
  assertAndContinue(APIUtil.isStorable(storableId), `Storable expected, got ${storableId}`);
  const nameMap = {
    [ModelTypeConstants.BOX]: 'Box',
    [ModelTypeConstants.PLATE]: 'Plate',
    [ModelTypeConstants.LOCATION]: 'Location',
  };
  const apiIdType = APIUtil.apiIdToType(storableId);

  if (nameMap.location.toUpperCase() === apiIdType.toUpperCase()) {
    return nameMap.location;
  } else if (nameMap.plate.toUpperCase() === apiIdType.toUpperCase()) {
    return nameMap.plate;
  } else if (nameMap.box.toUpperCase() === apiIdType.toUpperCase()) {
    return nameMap.box;
  } else {
    return 'Container';
  }
}

export function isFixedPlate(storable?: StorableSummary | null): storable is GridSummary {
  return isGrid(storable) && storable.storableSchema.plateSchemaType === PLATE_SCHEMA_TYPE.FIXED_PLATE;
}

export function isMatrixPlateOrBox(storable?: StorableSummary | null): storable is GridSummary {
  return isGrid(storable) && !isFixedPlate(storable);
}

/*
 * Checks if the given item is a child of a plate.
 */
export function isPlateWell(storable: StorableSummary): boolean {
  return (
    storable.parentStorableId != null &&
    APIUtil.apiIdToType(storable.parentStorableId) === ModelTypeConstants.PLATE
  );
}

/**
 * Checks if the given file is a fixed plate well.
 */
export function isFixedPlateWellForNav(file: ListableItem): boolean {
  return APIUtil.isContainer(file.id) && Boolean((file as Partial<ContainerSummary>).isFixedPlateWell);
}

/*
 * For the containers that are contained in plates, return their (deduplicated) parent storable IDs.
 */
export function getParentPlateIds(containers: Array<ContainerSummary>): Array<string> {
  // eslint-disable-next-line no-restricted-syntax
  return _.uniq(_.compact(_.pluck(containers, 'parentStorableId'))).filter(
    (parentStorableId) => APIUtil.apiIdToType(parentStorableId) === ModelTypeConstants.PLATE
  );
}

export function isIntegerPosition(position: string): boolean {
  return GRID_INTEGER_POSITION_REGEX.test(position);
}

/**
 * Get the alphanumeric position of a well given its index in a list of flattened wells.
 * @param index - 0-indexed index of the well in the plate
 * @param size - height/width of the plate
 * @returns - alphanumeric position of the well
 */
export function getAlphanumericPositionFromWellIndex(index: number, size: PlateMapSize): string {
  const {row, column} = getWellPositionFromIndex(index, size);
  return formatGridCoordinates(row, column);
}

/*
 * Returns formatted coordinates (e.g. A1) given row and column indices.
 * This is a light wrapper around Protocols util, which flips column and row headers.
 */
export function formatGridCoordinates(rowIndex: number, columnIndex: number): string {
  return util.toSpreadsheetIndex({row: columnIndex, column: rowIndex});
}

/*
 * Returns a row coordinate (A, B, C, ...) given a 0-indexed row index.
 */
export function formatGridRowCoordinate(rowIndex: number): string {
  return util.colNumberToLetters(rowIndex);
}

/*
 * Returns a 1-indexed column coordinate (1, 2, 3, ...) given a 0-indexed column index.
 */
export function formatGridColumnCoordinate(columnIndex: number): number {
  return util.rowIndexToNumber(columnIndex);
}

/*
 * Returns a 1-indexed position that corresponds to the given row and column indices.
 * Starts with 1 in the top-left corner, then increments in standard reading direction
 * (right-to-left then top-to-bottom).
 */
export function formatGridNumber(rowIndex: number, columnIndex: number, width: number): number {
  return width * rowIndex + columnIndex + 1;
}

/*
 * General purpose utility for formatting a row + column depending on whether it's for a box or for
 * a plate
 * Optional @{includeNoun} to include 'Position' or 'Well' in the formatted position
 */
export function formatGridPosition({
  gridType,
  rowIndex,
  columnIndex,
  width,
  includeNoun = false,
}: {
  gridType: GridType;
  rowIndex: number;
  columnIndex: number;
  width: number;
  includeNoun?: boolean;
}): string {
  let formatted =
    (gridType as string) === (ModelTypeConstants.BOX as string)
      ? `${formatGridNumber(rowIndex, columnIndex, width)} (${formatGridCoordinates(rowIndex, columnIndex)})`
      : formatGridCoordinates(rowIndex, columnIndex);

  if (includeNoun) {
    const containerNoun = (gridType as string) === (ModelTypeConstants.BOX as string) ? 'Position' : 'Well';
    formatted = `${containerNoun} ${formatted}`;
  }
  return `${formatted}`;
}

/*
 * Returns either grid number or coordinates depending on grid type. Use sparingly; prefer
 * formatGridPosition instead. Currently used for Transfer tables, which format for user input.
 */
export function formatPrimaryGridPosition({
  gridType,
  rowIndex,
  columnIndex,
  width,
}: {
  gridType: GridType;
  rowIndex: number;
  columnIndex: number;
  width: number;
}): string {
  const formatted =
    (gridType as string) === (ModelTypeConstants.BOX as string)
      ? formatGridNumber(rowIndex, columnIndex, width)
      : formatGridCoordinates(rowIndex, columnIndex);
  return `${formatted}`;
}

export function isInGridPositionFormat(position: Optional<string>): boolean {
  if (!position) {
    return false;
  }
  try {
    parseGridPosition({position, gridHeight: Number.MAX_SAFE_INTEGER, gridWidth: Number.MAX_SAFE_INTEGER});
  } catch (e) {
    if (e instanceof InvalidPositionError) {
      return false;
    }
    throw e;
  }
  return true;
}

/**
 * Converts an alphanumeric position (e.g. A1) to a row and column index.
 */
export function parseGridPosition({
  position,
  gridHeight,
  gridWidth,
}: {
  position?: string | null;
  gridHeight: number;
  gridWidth: number;
}): {rowIndex: number; columnIndex: number} {
  if (!position) {
    throw new NoPositionError();
  }
  let rowIndex: number;
  let columnIndex: number;
  if ('A' <= position[0].toUpperCase() && position[0].toUpperCase() <= 'Z') {
    // coordinate format
    // Ensure the regex is the same as BE's in parse_grid_coordinates in benchling/inventory/inventory_util.py
    const splitPosition = position.match(GRID_ALPHANUMERIC_POSITION_REGEX);
    // Match array with groupings include the full match at index 0
    if (!splitPosition || splitPosition.length !== 3) {
      throw new InvalidPositionFormatError();
    }
    rowIndex = util.colLettersToNumbers(splitPosition[1]);
    const parsedSplitPosition = parseInteger(splitPosition[2]);
    if (!parsedSplitPosition) {
      throw new InvalidPositionFormatError();
    }
    columnIndex = parsedSplitPosition - 1;
  } else {
    // number format
    // Check if position is purely numeric
    if (!GRID_INTEGER_POSITION_REGEX.test(position)) {
      throw new InvalidPositionFormatError();
    }
    let gridIndex: number | null = parseInteger(position);
    assert(gridIndex != null, 'Expected parseInteger result to not be null');
    gridIndex--;
    rowIndex = Math.floor(gridIndex / gridWidth);
    columnIndex = gridIndex % gridWidth;
  }
  if (!(0 <= rowIndex && rowIndex < gridHeight && 0 <= columnIndex && columnIndex < gridWidth)) {
    throw new PositionOutOfBoundsError();
  }
  return {rowIndex, columnIndex};
}

/**
 * Returns the id the nearest Location in a storable's ancestry.
 *
 * If the storable is a non-location storable, look at its parent storable.
 */
export function getNearestAncestralLocationId(
  storable?: {id: string; parentStorableId?: string | null} | null
): string | null {
  if (storable == null) {
    return null;
  } else if (APIUtil.isLocation(storable.id)) {
    return storable.id;
  } else if (!storable.parentStorableId) {
    return null;
  } else {
    assert(APIUtil.isLocation(storable.parentStorableId), 'Unexpected parent type');
    return storable.parentStorableId;
  }
}

function isContainer(sample: Batch | Container): sample is Container {
  return (sample as Container).contents != null;
}

function isBatch(sample: Batch | Container): sample is Batch {
  return (sample as Batch).file != null;
}

export function getSampleRoleIds(
  sampleRoles: SampleRoles | undefined,
  sampleRoleKey: 'sampleOwners' | 'restrictedSampleUsers'
): Array<string> {
  if (sampleRoles != null) {
    return sampleRoles[sampleRoleKey].map((sampleRole) => sampleRole.id);
  }
  return [];
}

export function getPossibleEditNamesDisabledError(canEditNamesAndBarcodes?: boolean): string | null {
  return !canEditNamesAndBarcodes ? "You do not have permission to edit this item's name." : null;
}

export function getPossibleEditBarcodesDisabledError(canEditBarcodes?: boolean): string | null {
  return !canEditBarcodes ? "You do not have permission to edit this item's barcode." : null;
}

export function getFixedPlateWellEditDisabledError(
  isFixedPlateWell: boolean,
  property: string
): string | null {
  return isFixedPlateWell ? `Well plate well ${property}s cannot be edited.` : null;
}

/*
 * Returns whether user can edit any properties, or take actions on a storable.
 *
 * Should be updated when more actions are granularized. Currently considers write and edit names &
 * barcodes. Sample controls permissions are granularized but also depend on write, and are excluded for now.
 */
export function canUserEditAnyPropertiesForStorable(storable: {permissions: Permissions}): boolean {
  return Boolean(
    storable.permissions.writable ||
      storable.permissions.canEditNamesAndBarcodes ||
      storable.permissions.canEditBarcodes
  );
}

/**
 * Whether the grid is too large for use with the new plate annotation & fill features.
 */
export function isGridSchemaTooBigForNewPlateFeatures(schema: GridSchema): boolean {
  return schema.height > PLATE_MAP_MAX_GRID_HEIGHT || schema.width > PLATE_MAP_MAX_GRID_WIDTH;
}

/**
 * Whether a banner should be shown explaining that the new annotation and fill plate features cannot be
 * shown because the well plate being viewed is too large for the new features
 */
export function shouldShowNewFeatureBanner(grid: Grid): boolean {
  return isFixedPlate(grid) && isGridSchemaTooBigForNewPlateFeatures(grid.storableSchema);
}

/**
 * Whether the new features for annotating and filling plates should be shown.
 * Currently only available for well plates of dimensions no greater than 24 x 16 wells.
 */
export function hasNewPlateAnnotationFillFeatures(grid: Grid): boolean {
  return isFixedPlate(grid) && !isGridSchemaTooBigForNewPlateFeatures(grid.storableSchema);
}

/**
 * Gets the flattened 1D indices of the given cells on the given grid based on their positions.
 */
export function getCellsAsIndices(cells: Array<GridCellSummary>, grid: Grid): Array<number> {
  return cells.map<number>((cell) =>
    getFlattenedWellIndex(cell.rowIndex, cell.columnIndex, {
      numRows: grid.storableSchema.height,
      numColumns: grid.storableSchema.width,
    })
  );
}

/**
 * Returns available capacity, based on total & occupied capacity.
 *
 * If location is over capacity, returns 0.
 *
 * Keep in sync with benchling.inventory.storables.capacity.get_available_capacity
 */
export function getAvailableCapacity(totalCapacity: number, occupiedCapacity: number): number {
  return Math.max(totalCapacity - occupiedCapacity, 0);
}

/**
 * Returns available space string, based on total & occupied capacity.
 *
 * If location is over capacity, returns 0 available space.
 *
 * Keep in sync with benchling.inventory.storables.capacity.get_any_storable_available_space_str
 */
export function getAvailableSpaceDisplayString(totalCapacity: number, occupiedCapacity: number): string {
  return `${getAvailableCapacity(totalCapacity, occupiedCapacity)} / ${totalCapacity}`;
}

export function isGridSummary(storable: StorableSummary): storable is GridSummary {
  return isGridId(storable.id);
}
